import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Dispatch, SetStateAction } from 'react';

import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useRedisWritesBlocked } from '@/apis/call';
import { useReleaseContacts } from './hooks';
import type { ToggleValue } from './types';

export type { ToggleValue } from './types';

type ReleaseContactsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  isACoordinator: boolean;
  releaseContactsState: string;
  listId: number;
  listName: string;
  disableMeToggle: boolean;
  toggleValue: ToggleValue;
  setToggleValue: Dispatch<SetStateAction<ToggleValue>>;
  chosenUserName?: string;
  chosenUserSub?: string;
};

const ReleaseContactsDialog = ({
  isOpen,
  onClose,
  isACoordinator,
  releaseContactsState,
  listId,
  listName,
  disableMeToggle,
  toggleValue,
  setToggleValue,
  chosenUserName = '',
  chosenUserSub = '',
}: ReleaseContactsDialogProps) => {
  const { mutateAsync: releaseContacts } = useReleaseContacts(listId);
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();

  const handleRelease = async () => {
    if (writesBlocked) return;
    try {
      await releaseContacts({ chosenUserSub, toggleValue, releaseContactsState });
      onClose();
    } catch {
      enqueueErrorNotification('Contacts could not be released.');
    }
  };

  const dialogTitle =
    chosenUserName && chosenUserSub
      ? 'Release remaining contacts'
      : isACoordinator
        ? 'Release remaining contacts for'
        : 'Release remaining contacts';

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={onClose}
      dialogTitle={dialogTitle}
      primaryButton={{
        text: 'Release',
        onClick: handleRelease,
        disabled: writesBlocked,
      }}
    >
      <div>
        {isACoordinator && !chosenUserName && !chosenUserSub && (
          <>
            <Stack
              spacing={2}
              sx={{
                alignItems: 'center',
                mb: 0,
                mt: 2,
                marginTop: 0
              }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={toggleValue}
                onChange={async (_e, value) => {
                  if (value) {
                    setToggleValue(value);
                  }
                }}
              >
                <ToggleButton sx={{ width: '140px' }} value="me" disabled={disableMeToggle}>
                  Me
                </ToggleButton>
                <ToggleButton sx={{ width: '140px' }} value="all">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <p>
              Are you sure you want to release the remaining {releaseContactsState} people in the
              list <strong>{listName}</strong>?
            </p>
          </>
        )}
        {!isACoordinator && !chosenUserName && !chosenUserSub && (
          <p style={{ marginTop: 0 }}>
            Are you sure you want to release the remaining {releaseContactsState} people in the list{' '}
            <strong>{listName}</strong>?
          </p>
        )}

        {chosenUserName && chosenUserSub && (
          <p style={{ marginTop: 0 }}>
            Are you sure you want to release the remaining {releaseContactsState} people for{' '}
            <strong>{chosenUserName}</strong>?
          </p>
        )}

        <p>
          <em>Note: this will release contacts so they can be reassigned to other callers.</em>
        </p>
      </div>
    </AgDialog>
  );
};

export default ReleaseContactsDialog;
